// Explorer Residual Risk Service - Residual risk calculation for Explorer assessments
// Uses Explorer-specific Redux state (explorerRisks, not modelRisks)
import {
  generateResidualRisk,
  analyzeActionPlan,
  saveResidualRisk,
  RESIDUAL_RISK_RATINGS,
} from '../../redux/slices/residualRiskSlice';

export class ExplorerResidualRiskService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeResidualAssessment(areaKey, areaDefinition, inheritedRisks, progressCallback) {
    try {
      console.log(`[ExplorerResidualRiskService] Starting residual assessment for area: ${areaKey}`);
      if (progressCallback) progressCallback('residual');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Explorer risks (not modelRisks)
      const explorerRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.explorerRisks || {};
      const areaRisks = explorerRisks[areaKey]?.areaRisks || {};

      const evaluatedRisks = [];

      console.log(`[ExplorerResidualRiskService] State source for area: ${areaKey}`);
      console.log(`[ExplorerResidualRiskService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[ExplorerResidualRiskService] Available risk categories:`, Object.keys(areaRisks));

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        if (Array.isArray(riskArray)) {
          riskArray.forEach(risk => {
            if (risk.status === 'Saved' && risk.priority) {
              evaluatedRisks.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      console.log(`[ExplorerResidualRiskService] Found ${evaluatedRisks.length} evaluated risks`);

      // Get fresh control state
      const freshControlState = currentState.controls || {};

      console.log(`[ExplorerResidualRiskService] Checking for saved controls...`);
      console.log(`[ExplorerResidualRiskService] Available savedControls keys:`, Object.keys(freshControlState?.savedControls || {}));

      // Find risks with saved actions
      const risksWithActions = evaluatedRisks.filter(risk => {
        // Ensure this risk is from the current area
        if (risk.areaKey !== areaKey) {
          console.log(`[ExplorerResidualRiskService] SKIPPING risk from wrong area: "${risk.riskName}"`);
          return false;
        }

        const allControls = freshControlState?.savedControls?.[risk.riskId] || [];
        const modelControls = allControls.filter(control => control.modelId === this.modelId);
        console.log(`[ExplorerResidualRiskService] Risk "${risk.riskName}" (${risk.riskId}): ${modelControls.length} saved controls`);
        return modelControls.length > 0;
      });

      console.log(`[ExplorerResidualRiskService] Found ${risksWithActions.length} risks with actions for residual assessment`);

      let residualAssessments = 0;

      for (const risk of risksWithActions) {
        try {
          // PARALLEL EXECUTION: Run both API calls concurrently
          console.log(`[ExplorerResidualRiskService] Starting parallel assessment for: ${risk.riskName}`);
          const startTime = Date.now();

          await Promise.all([
            // Analyze action plan effectiveness
            this.dispatch(analyzeActionPlan({
              assessmentUuid: this.assessmentUuid,
              modelId: this.modelId,
              riskId: risk.riskId,
              riskName: risk.riskName,
              assessmentType: 'explorer' // Mark as Explorer
            })),
            // Generate residual risk assessment
            this.dispatch(generateResidualRisk({
              assessmentUuid: this.assessmentUuid,
              modelId: this.modelId,
              riskId: risk.riskId,
              assessmentType: 'explorer' // Mark as Explorer
            }))
          ]);

          const elapsed = Date.now() - startTime;
          console.log(`[ExplorerResidualRiskService] Parallel assessment completed in ${elapsed}ms for: ${risk.riskName}`);

          // LIVE UPDATE: Stream action plan analysis completion
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('actionPlanAnalyzed', {
              riskName: risk.riskName,
              riskId: risk.riskId,
              areaKey
            });
          }

          // Get the generated residual risk data from RTK slice
          const updatedState = getState();
          const residualRiskData = updatedState.residualRiskRTK?.byAssessment?.[this.assessmentUuid]?.[risk.riskId] || {};

          // Validate rating is in enum
          let residualRating = residualRiskData.residual_rating;
          if (!residualRating || !RESIDUAL_RISK_RATINGS.includes(residualRating)) {
            console.error(`[ExplorerResidualRiskService] Invalid/missing rating "${residualRating}" - skipping save for this risk`);
            continue;
          }

          // Save residual risk data
          const residualData = {
            modelId: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskId: risk.riskId,
            riskName: risk.riskName,
            priority: risk.priority,
            evaluator_id: 'explorer-autopilot@agenthuginn.ai',
            comments: `Generated by ExplorerRiskAutopilot for area: ${areaKey}`,
            residual_rating: residualRating,
            residual_explanation: residualRiskData.residual_explanation || '',
            action_plan_rating: residualRiskData.action_plan_rating || '',
            action_plan_analysis: residualRiskData.action_plan_analysis || '',
            action_plan_short_summary: residualRiskData.action_plan_short_summary || '',
            assessmentType: 'explorer' // Mark as Explorer
          };

          this.dispatch(saveResidualRisk(residualData));
          residualAssessments++;

          // LIVE UPDATE: Stream residual risk assessment completion
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('residualRiskCalculated', {
              riskName: risk.riskName,
              riskId: risk.riskId,
              residualRating: residualRating,
              actionPlanRating: residualRiskData.action_plan_rating || '',
              assessedCount: residualAssessments,
              totalRisks: risksWithActions.length,
              areaKey
            });
          }

        } catch (residualError) {
          console.error(`[ExplorerResidualRiskService] Residual assessment failed for ${risk.riskName}:`, residualError);
        }
      }

      return {
        risksWithActions: risksWithActions.length,
        residualAssessments,
        areaKey
      };

    } catch (error) {
      console.error('[ExplorerResidualRiskService] Residual assessment failed:', error);
      throw error;
    }
  }
}
